import type { ShareAudience, ShareEntryInput } from '@clockwise/clients/report-definitions';
import { ApiError } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Briefcase, Globe, ShieldCheck, User as UserIcon, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Combobox } from '@/components/custom/combobox';
import { ScrollDialogBody, ScrollDialogContent } from '@/components/custom/scroll-dialog';
import {
  ServiceRoleAutocomplete,
  serviceRolePreloadQuery,
} from '@/components/custom/service-role-autocomplete';
import { lookupFailureMessage } from '@/components/custom/share-lookup-failures';
import { roleKind } from '@/components/custom/share-role-kind';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { serviceRolesApi, systemRolesApi, usersApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** One stored share, as either resource returns it. */
export interface ShareRow {
  audience: ShareAudience;
  subjectId: string | null;
}

interface Props {
  /** What is being shared: its name for the copy, its shares for the list. */
  subject: { id: string; name: string; shares: readonly ShareRow[] };
  /**
   * The noun in the copy — "report", "dashboard".
   *
   * A parameter rather than a generic phrase, because "Only you. Nobody else
   * can find this yet." reads as though something is missing from the sentence,
   * and this dialog's whole job is to be unambiguous about what is being
   * handed over.
   */
  noun: string;
  /** Replace the whole list. The caller owns which endpoint that is. */
  onSave: (shares: ShareEntryInput[]) => Promise<unknown>;
  /** Invalidated on success, so the list the owner came from updates. */
  invalidateKey: readonly unknown[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** A share entry as the list renders it, before it goes back to the server. */
type Entry = ShareEntryInput & { label: string };

/**
 * Live service roles only: a retired role has nobody new joining it, so it is
 * not worth offering. One already shared with is still NAMED — that lookup is
 * by id and ignores this filter.
 */
const SERVICE_ROLE_QUERY = serviceRolePreloadQuery({ active: true });

/** The picker's list, as its cache entry holds it. */
type ServiceRoleList = Awaited<ReturnType<typeof serviceRolesApi.list>>;

/**
 * Who may open this thing.
 *
 * ONE dialog for reports and dashboards. They are the same artefact wearing
 * different nouns — owned by one person, shared by user / role / everyone,
 * holding a question rather than an answer — and the fiddly parts here
 * (resolving a name for an id outside the first page of twenty, the
 * everyone-switch that has to undo, the 403 that must not read as an empty
 * tenant) are worth solving once rather than twice.
 *
 * WHAT THIS DIALOG GRANTS is the right to run a definition — never a set of
 * rows. Everyone named here opens it and it re-runs under their own
 * `report.view` scope, so adding somebody can never show them a figure they
 * could not already reach elsewhere in the product. That is why there is no
 * warning copy, no "are you sure", and no data-classification step: there is
 * nothing here to leak, and a dialog that implied otherwise would teach people
 * a rule the system does not have.
 *
 * TWO KINDS OF ROLE, and they are not interchangeable. A SERVICE role is the
 * job on somebody's employee record — Controller, Bookkeeper — which is how a
 * finance team says "who should see this". A SYSTEM role is the permission set
 * they sign in with. Each has its own picker and its own caption in the list,
 * because the two catalogs can hold the same name and a share that silently
 * meant the other one would reach the wrong people.
 *
 * The list is edited as a LIST and saved whole (`PUT .../shares`), matching the
 * API. Closing without saving changes nothing.
 */
export function ShareDialog({ subject, noun, onSave, invalidateKey, open, onOpenChange }: Props) {
  const queryClient = useQueryClient();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [personQuery, setPersonQuery] = useState('');

  // Re-seeded when the dialog OPENS, and on nothing else.
  //
  // Keyed on the id, not on the shares array: that array gets a fresh
  // identity on every refetch of the detail query, so a background refetch —
  // alt-tab away and back is enough — used to fire this effect mid-edit and
  // silently replace the list the owner was building with the server's.
  const subjectId = subject.id;
  const initialShares = useRef(subject.shares);
  initialShares.current = subject.shares;
  useEffect(() => {
    if (!open) return;
    setEntries(
      initialShares.current.map((s) => ({
        audience: s.audience,
        subjectId: s.subjectId,
        // Filled in properly once the lookups below resolve; the id is the
        // honest placeholder until then rather than a guessed name.
        label: s.subjectId ?? 'Everyone',
      })),
    );
  }, [open, subjectId]);

  const people = useQuery({
    queryKey: queryKeys.users.list({ q: personQuery || undefined, limit: 20, active: true }),
    queryFn: () => usersApi.list({ q: personQuery || undefined, limit: 20, active: true }),
    enabled: open,
  });
  const systemRoles = useQuery({
    queryKey: queryKeys.systemRoles.list({ limit: 100 }),
    queryFn: () => systemRolesApi.list({ limit: 100 }),
    enabled: open,
  });

  /**
   * The people this is ALREADY shared with, fetched by id.
   *
   * Separate from the search above, which returns one page of twenty: a share
   * with somebody outside that page had no name to resolve to and rendered as a
   * raw uuid — in the list and in its remove button's accessible name.
   * `findByIds` is bounded by the ids asked for and never capped.
   */
  const sharedUserIds = subject.shares
    .filter((s) => s.audience === 'user' && s.subjectId)
    .map((s) => s.subjectId as string);
  const namedPeople = useQuery({
    queryKey: queryKeys.users.byIds(sharedUserIds),
    queryFn: () => usersApi.findByIds(sharedUserIds),
    enabled: open && sharedUserIds.length > 0,
  });

  /** The service roles this is ALREADY shared with — by id, so a since-retired
   *  role still has a name here though the picker no longer offers it. */
  const sharedServiceRoleIds = subject.shares
    .filter((s) => s.audience === 'service_role' && s.subjectId)
    .map((s) => s.subjectId as string);
  const namedServiceRoles = useQuery({
    queryKey: queryKeys.serviceRoles.byIds(sharedServiceRoleIds),
    queryFn: () => serviceRolesApi.findByIds(sharedServiceRoleIds),
    enabled: open && sharedServiceRoleIds.length > 0,
  });

  // Names for the ids already stored. Resolved from the lookups above,
  // so a share added in this session and one loaded from the server read
  // identically.
  useEffect(() => {
    const findPerson = (id: string | null | undefined) =>
      namedPeople.data?.data.find((u) => u.id === id) ?? people.data?.data.find((u) => u.id === id);
    const nameOf = (e: Entry): string => {
      if (e.audience === 'everyone') return 'Everyone';
      if (e.audience === 'role') {
        return systemRoles.data?.data.find((r) => r.id === e.subjectId)?.name ?? e.label;
      }
      if (e.audience === 'service_role') {
        // The by-id fetch first, then whatever the picker below has already
        // loaded. That list is READ from the cache, never fetched here: it is
        // the picker's query, and the usual way to arrive at this branch — add
        // a role, save, reopen — leaves it sitting there with the name in it,
        // so the row need not show a raw id while the by-id fetch is in
        // flight, nor for good if that one request fails.
        const cached = queryClient.getQueryData<ServiceRoleList>(
          queryKeys.serviceRoles.list(SERVICE_ROLE_QUERY),
        );
        const role =
          namedServiceRoles.data?.data.find((r) => r.id === e.subjectId) ??
          cached?.data.find((r) => r.id === e.subjectId);
        return role?.name ?? e.label;
      }
      // An audience newer than this bundle keeps its placeholder rather than
      // being looked up as a person it is not.
      if (e.audience !== 'user') return e.label;
      const person = findPerson(e.subjectId);
      return person ? `${person.firstName} ${person.lastName}` : e.label;
    };
    setEntries((prev) => {
      const next = prev.map((e) => ({ ...e, label: nameOf(e) }));
      // Only re-set when something actually resolved — otherwise this effect
      // and its own setState loop forever.
      return next.some((e, i) => e.label !== prev[i]?.label) ? next : prev;
    });
  }, [people.data, systemRoles.data, namedPeople.data, namedServiceRoles.data, queryClient]);

  // G15: a refused lookup must not read as an empty tenant. Each picker reads
  // another domain's list, behind that domain's own view permission, which
  // `report.share` does not imply — so a reporting-only role would otherwise
  // find silently empty pickers and no way to tell a 403 from "nobody to share
  // with". Kept per lookup, so the message says what each one is — see
  // `lookupFailureMessage`.
  //
  // The service-role LIST is not here: the picker owns that fetch and reports
  // its own failure in its dropdown, so only the by-id lookup is this dialog's.
  const lookupFailure = lookupFailureMessage(
    [
      { what: 'people', permission: 'user.view', error: people.error ?? namedPeople.error },
      {
        what: 'service roles',
        permission: 'service_role.view',
        error: namedServiceRoles.error,
      },
      { what: 'system roles', permission: 'system_role.view', error: systemRoles.error },
    ].filter((l) => l.error),
  );

  const everyone = entries.some((e) => e.audience === 'everyone');

  const save = useMutation({
    mutationFn: () => onSave(entries.map(({ audience, subjectId }) => ({ audience, subjectId }))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invalidateKey });
      toast.success('Sharing updated');
      onOpenChange(false);
    },
    // G15: a failed share must say so. Silently closing would leave the owner
    // believing the report is shared when it is not.
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not update sharing'),
  });

  // The list scrolls rather than growing the dialog, and additions land at its
  // end — so past the first few rows a new share would arrive out of sight and
  // the picker would look as though it had done nothing. Flagged by `add`, the
  // one place that appends, and only when it does: the seed on open grows the
  // list too, and that must leave it at the top.
  const listRef = useRef<HTMLDivElement>(null);
  const revealLast = useRef(false);
  useEffect(() => {
    if (!revealLast.current) return;
    revealLast.current = false;
    // The list's own scroll and nothing else: `scrollIntoView` would also
    // scroll the dialog body, carrying off the picker that was just used.
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [entries]);

  const add = (entry: Entry) => {
    // Choosing somebody already listed changes nothing — and must not leave
    // the flag set for whatever happens to change the list next.
    if (entries.some((e) => e.audience === entry.audience && e.subjectId === entry.subjectId)) {
      return;
    }
    revealLast.current = true;
    setEntries((prev) => [...prev, entry]);
  };

  const remove = (index: number) => setEntries((prev) => prev.filter((_, i) => i !== index));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ScrollDialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Share “{subject.name}”</DialogTitle>
          <DialogDescription>
            Sharing gives people the right to open this {noun}. Everyone sees it under their own
            access, so it can never show them data they couldn’t already reach.
          </DialogDescription>
        </DialogHeader>

        {/* `-mx-5 px-5`: room at the side for the list's scrollbar — see below. */}
        <ScrollDialogBody className="-mx-5 px-5">
          {lookupFailure ? (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm"
            >
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
              <p className="min-w-0">
                <span className="font-medium text-destructive">{lookupFailure.heading}</span>{' '}
                {lookupFailure.detail}
              </p>
            </div>
          ) : null}

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="share-everyone" className="flex items-center gap-2">
                <Globe className="size-4 text-muted-foreground" aria-hidden />
                Everyone in the company
              </Label>
              <p className="text-muted-foreground text-xs">
                Anyone who can view reports at all will find this {noun}.
              </p>
            </div>
            <Switch
              id="share-everyone"
              checked={everyone}
              // The widest grant is ADDED, never substituted. It used to replace
              // the named entries, on the reasoning that "…and also Jane" grants
              // nothing once everybody can run it — true, but the switch then
              // did not undo: turning it back off left an empty list, and saving
              // revoked people who had nothing to do with the toggle. Flipping a
              // switch twice has to land where it started, so the narrower rows
              // stay put and the caption below says they are redundant for now.
              onCheckedChange={(on) =>
                on
                  ? add({ audience: 'everyone', subjectId: null, label: 'Everyone' })
                  : setEntries((prev) => prev.filter((e) => e.audience !== 'everyone'))
              }
            />
          </div>

          {!everyone && (
            <>
              <div className="space-y-1.5">
                <Label>Add a person</Label>
                <Combobox
                  items={people.data?.data ?? []}
                  value={null}
                  selectedItem={null}
                  loading={people.isFetching}
                  onQueryChange={setPersonQuery}
                  getId={(u) => u.id}
                  getLabel={(u) => `${u.firstName} ${u.lastName}`}
                  placeholder="Search people…"
                  onChange={(u) =>
                    u &&
                    add({
                      audience: 'user',
                      subjectId: u.id,
                      label: `${u.firstName} ${u.lastName}`,
                    })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="share-service-role">Add a service role</Label>
                <p className="text-muted-foreground text-xs">
                  Everyone whose employee record has this role.
                </p>
                {/* The canonical service-role picker, used as an ADD control:
                    it never holds a value, and what is already in the list
                    below drops out of the dropdown. */}
                <ServiceRoleAutocomplete
                  id="share-service-role"
                  value={null}
                  active
                  excludeIds={entries
                    .filter((e) => e.audience === 'service_role' && e.subjectId)
                    .map((e) => e.subjectId as string)}
                  placeholder="Search service roles…"
                  onChange={(r) =>
                    r && add({ audience: 'service_role', subjectId: r.id, label: r.name })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label>Add a system role</Label>
                <p className="text-muted-foreground text-xs">
                  Everyone who signs in with this set of permissions.
                </p>
                <Combobox
                  items={systemRoles.data?.data ?? []}
                  value={null}
                  selectedItem={null}
                  getId={(r) => r.id}
                  getLabel={(r) => r.name}
                  filterFn={(r, q) => r.name.toLowerCase().includes(q.toLowerCase())}
                  placeholder="Search system roles…"
                  onChange={(r) => r && add({ audience: 'role', subjectId: r.id, label: r.name })}
                />
              </div>
            </>
          )}

          {/* The one part of the body that gives up height. The list is a FIXED
              height, not a cap — so the dialog is the same size with no shares
              as with forty, and the pickers never move under the cursor — and
              that height lands mid-row on purpose: a cut-off row is what says
              there is more below. In a window too short for it the list
              shrinks, down to `min-h-32` for this section, before the body
              around it starts to scroll. */}
          <div className="@container flex min-h-32 flex-col gap-1.5">
            <Label>Shared with</Label>
            {/* THE SCROLLBAR GOES BESIDE THE ROWS, NOT ON THEM. A scrollbar is
                drawn inside the box that scrolls, and on macOS and phones it
                floats over the content — so with the rows running to the edge
                of that box it sat on top of their borders. The box therefore
                reaches `-mr-5` further right than the rows do, into the
                dialog's own padding, and the scrollbar lives in that strip.

                The rows are held to `100cqw` — the width of this section, the
                container — so they line up with the pickers above whether the
                scrollbar floats (the strip is empty space) or takes up room
                (`scrollbar-gutter` reserves it there from the start, so one
                arriving with the sixth share cannot narrow every row and move
                every remove button). */}
            <div
              ref={listRef}
              data-testid="share-list"
              className="-mr-5 h-56 min-h-0 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]"
            >
              <div className="w-[100cqw]">
                {entries.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    Only you. Nobody else can find this {noun} yet.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {entries.map((entry, index) => (
                      <li
                        key={`${entry.audience}:${entry.subjectId ?? 'all'}`}
                        className="flex items-center justify-between rounded-md border px-2.5 py-1.5 text-sm"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          {entry.audience === 'everyone' ? (
                            <Globe
                              className="size-3.5 shrink-0 text-muted-foreground"
                              aria-hidden
                            />
                          ) : entry.audience === 'role' ? (
                            <ShieldCheck
                              className="size-3.5 shrink-0 text-muted-foreground"
                              aria-hidden
                            />
                          ) : entry.audience === 'service_role' ? (
                            <Briefcase
                              className="size-3.5 shrink-0 text-muted-foreground"
                              aria-hidden
                            />
                          ) : (
                            <UserIcon
                              className="size-3.5 shrink-0 text-muted-foreground"
                              aria-hidden
                            />
                          )}
                          <span className="truncate">{entry.label}</span>
                          {roleKind(entry.audience) && (
                            <span className="shrink-0 text-muted-foreground text-xs">
                              ({roleKind(entry.audience)})
                            </span>
                          )}
                          {/* Says what the switch used to say by deleting them:
                          while everyone can run this, naming somebody grants
                          nothing extra. They are kept so turning the switch
                          back off restores the sharing that was there. */}
                          {everyone && entry.audience !== 'everyone' && (
                            // `truncate`, not `shrink-0`: on a phone this caption
                            // and the kind beside it are wider than the row, and
                            // one that cannot give way pushes the remove button
                            // out of reach.
                            <span className="truncate text-muted-foreground text-xs">
                              (already covered by Everyone)
                            </span>
                          )}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-6"
                          // The kind is part of the name: a system role and a
                          // service role may both be called "Controller", and two
                          // buttons with one accessible name cannot be told apart.
                          aria-label={`Stop sharing with ${entry.label}${
                            roleKind(entry.audience) ? ` (${roleKind(entry.audience)})` : ''
                          }`}
                          onClick={() => remove(index)}
                        >
                          <X className="size-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </ScrollDialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save sharing'}
          </Button>
        </DialogFooter>
      </ScrollDialogContent>
    </Dialog>
  );
}
